import Navigation from "@/components/Navigation";
import FooterWithNewsletter from "@/components/ui/footer-column";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FeatureCard } from "@/components/ui/grid-feature-cards";
import { InfiniteTestimonialSlider } from "@/components/ui/infinite-testimonial-slider";
import { ArrowRight, Sparkles, MapPin, Calendar, Printer, Settings, Zap, Rocket, Share2 } from "lucide-react";
import { motion, LayoutGroup, useReducedMotion } from "motion/react";
import PricingPreview from "@/components/PricingPreview";
import CTASection from "@/components/CTASection";
import ClientLogosSection from "@/components/ClientLogosSection";
import FinalCTA from "@/components/FinalCTA";
import SeoFaqSection from "@/components/SeoFaqSection";

type ViewAnimationProps = {
  delay?: number;
  className?: React.ComponentProps<typeof motion.div>['className'];
  children: React.ReactNode;
};

function AnimatedContainer({ className, delay = 0.1, children }: ViewAnimationProps) {
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      initial={{ filter: 'blur(4px)', translateY: -8, opacity: 0 }}
      whileInView={{ filter: 'blur(0px)', translateY: 0, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const billingFaqs = [
  { question: "How does billing work?", answer: "Choose your plan, enter your payment details, and receive invoices monthly or annually—simple, transparent, and hassle-free. You'll be charged when you sign up and again on each renewal date. Your next billing date is always shown on your Subscription Page." },
  { question: "What payment methods do you accept?", answer: "We accept all major credit and debit cards (Visa, Mastercard, American Express, JCB), and Link (by Stripe). PayPal is not supported at this time." },
  { question: "Can I pay by invoice?", answer: "Yes you can. However, payment by invoice is available only for Enterprise plan users. <a href='/enterprise#meet-the-crew' class='text-primary hover:underline'>Reach out to our sales team</a> to set this up." },
  { question: "What currencies does gimmefy accept?", answer: "All plans are priced in US dollars (USD). For your reference, our payment processor shows local currency equivalents at checkout. Enterprise clients in select regions can request invoices in additional major currencies." },
  { question: "Can I add more than 5 team members to my organization?", answer: "Yes, but you'll need to upgrade to an Enterprise plan! For bigger teams, simply <a href='/enterprise#meet-the-crew' class='text-primary hover:underline'>contact the sales team</a>, and we'll tailor an Enterprise plan just for you." },
  { question: "Can I change my plan mid-cycle?", answer: "Yes! Upgrade, downgrade, or switch plans anytime by clicking Edit Subscription on your Subscription Page. Changes take effect on your next billing date, which is always visible on your account." },
  { question: "How can I purchase add-on credits? Do credits expire?", answer: "Click Buy Credits (in \"View Detailed Usage History\" on your Subscription Page) to top up anytime. Purchased credits never expire—use them whenever you need." },
  { question: "Can I pause my subscription?", answer: "Yes! Please <a href='/contact' class='text-primary hover:underline'>contact our support team</a> and we'll help with a flexible pause option." },
  { question: "What if I need to cancel?", answer: "You can cancel anytime and instantly, right from your account—no need to call or email. Just go to your Subscription Page, click Edit Subscription (in \"View Detailed Usage History\"), and follow the steps. Your cancellation is immediate and fully self-managed." },
  { question: "Still have a question about billing, payments, or your plan?", answer: "Drop us a message via our <a href='/contact' class='text-primary hover:underline'>contact form</a>. We're always happy to help!" }
];

const RetailPage = () => {
  return (
    <LayoutGroup>
      <div className="min-h-screen bg-background">
        <Navigation />

        {/* Hero Section */}
        <section className="py-32 bg-gradient-to-br from-background via-background to-primary/5 relative overflow-hidden">

          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              className="max-w-5xl mx-auto text-center space-y-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="space-y-6">
                <h1 id="retail-hero-heading" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground leading-tight">
                Pro-Level Marketing, All-In-One. <br/>
                  <span className="bg-gradient-primary bg-clip-text text-transparent">Big Results on a Lean Budget.</span>
                </h1>
                <p id="retail-hero-description" className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Run local ads, seasonal campaigns, and branded visuals—start to finish, all in gimmefy. Lower costs, no messy tool stacks, and every asset shaped by marketing smarts and advanced AI.
                </p>
              </div>

              <motion.div
                className="flex flex-col items-center gap-4 pt-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button id="retail-cta-start-free" variant="hero" size="lg" className="group text-lg sm:text-xl px-8 py-6 sm:px-12 sm:py-8 min-h-[48px] w-full sm:w-auto" onClick={() => window.location.href = '/signup/free'}>
                    Start Free
                    <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
                <p id="retail-cta-note" className="text-sm text-muted-foreground">No credit card. Get free credits.</p>
              </motion.div>

              <ClientLogosSection />

              {/* Retail-Ready Features Section - moved up */}
              <motion.div
                className="pt-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                <section className="py-24 bg-background">
                  <div className="container mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                      {/* Left Column - Title, Subtitle & Information */}
                      <div className="space-y-12">
                        {/* Title and Subtitle */}
                        <motion.div
                          className="text-left"
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.8 }}
                        >
                          <h2 className="text-5xl md:text-6xl font-bold text-foreground mb-6">Retail-Ready Features</h2>
                          <p className="text-xl text-muted-foreground max-w-3xl">
                            Everything you need to market your store like a pro
                          </p>
                        </motion.div>

                        {/* Feature Cards */}
                        <div className="space-y-8">
                          {[
                            {
                              icon: MapPin,
                              title: "Location-Aware Advertising",
                              description: "Create campaigns based on your customers' language or cultural nuances"
                            },
                            {
                              icon: Calendar,
                              title: "Seasonal Promo Playbooks",
                              description: "Ready-made campaigns for holidays, events, and peak shopping seasons"
                            },
                            {
                              icon: Printer,
                              title: "Poster & Carousel Generator",
                              description: "Professional in-store signage and social media carousels in seconds"
                            }
                          ].map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.8 + (index * 0.1), duration: 0.6 }}
                              >
                                <Card className="p-6 bg-gradient-card border-border shadow-elegant hover:shadow-elegant-hover transition-all duration-300 group hover:scale-105">
                                  <div className="flex items-start space-x-4">
                                    <div className="w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                                      <Icon className="h-8 w-8 text-white" />
                                    </div>
                                    <div className="space-y-2">
                                      <h3 className="text-xl font-bold text-foreground">{feature.title}</h3>
                                      <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                                    </div>
                                  </div>
                                </Card>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Right Column - Image */}
                      <motion.div
                        className="relative lg:order-last order-first"
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4, duration: 0.8 }}
                      >
                        <div className="relative">
                          <img
                            src="/lovable-uploads/38465ef6-5b2c-4114-b395-257f1a561bb9.jpg"
                            alt="Retail business owner in orange dress working with products and packages"
                            className="w-full max-w-sm mx-auto lg:max-w-none h-auto rounded-2xl shadow-elegant"
                            width={456}
                            height={591}
                          />
                          <div className="absolute inset-0 bg-gradient-primary/10 rounded-2xl"></div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </section>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Pain → Promise Section */}
        <section className="py-32 bg-gradient-accent relative">
          <div className="container mx-auto px-6">
            <motion.div
              className="max-w-5xl mx-auto text-center space-y-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="space-y-6">
                <p className="text-2xl md:text-3xl text-muted-foreground">
                  Juggling stock, staff, and socials kills momentum.
                </p>
                <p className="text-2xl md:text-3xl text-foreground font-semibold leading-relaxed">
                  gimmefy auto-builds local ads, SMS bursts, and storefront posters from one prompt—so you stay top of feed and top of mind.
                </p>
                <div className="inline-flex items-center space-x-3 bg-gradient-primary/20 backdrop-blur-sm rounded-full px-8 py-4 border border-primary/30">
                  <Sparkles className="h-6 w-6 text-primary-glow" />
                  <span className="text-foreground font-bold text-xl">
                    Retailers using gimmefy see a <span className="text-primary-glow">30%</span> jump in website traffic in the first month.
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <CTASection title="Know a retailer ready for fresh marketing?" subtitle="Invite a retailer to try gimmefy and reward them with bonus signup credits for their first campaign." buttonText="Tell a friend" buttonLink="/tellafriend" />

        {/* Why Retail & Small Businesses Choose gimmefy */}
        <section className="py-32 bg-background">
          <div className="container mx-auto px-6">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
                Why Retail & Small Businesses Choose gimmefy
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  icon: MapPin,
                  title: "Hard-Hitting Local Content",
                  description: "Campaigns that win attention in your neighbourhood, backed by big-brand expertise and proven marketing frameworks."
                },
                {
                  icon: Sparkles,
                  title: "Premium Localised Campaigns",
                  description: "Seasonal, multi-market launches without extra cost by converting ads into 40+ languages in the same chat window."
                },
                {
                  icon: Printer,
                  title: "Big Impact, Small Budget",
                  description: "Look premium without the big-agency fees with on-brand visuals, copy, and videos for every channel."
                }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Card className="p-8 h-full bg-gradient-card border-border shadow-elegant hover:shadow-elegant-hover transition-all duration-300 group hover:scale-105">
                      <div className="text-center space-y-6">
                        <div className="w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                          <Icon className="h-8 w-8 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-foreground">{item.title}</h3>
                        <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-32 bg-background">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-7xl mx-auto">
              {/* Left column - Headline */}
              <AnimatedContainer className="text-left">
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                  How It Works
                </h2>
              </AnimatedContainer>

              {/* Right column - Cards in 2x2 grid */}
              <AnimatedContainer
                delay={0.4}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8"
              >
                {[
                  {
                    icon: Settings,
                    title: "Brand Vault",
                    description: "Logo, palette, store info—done.",
                    number: 1,
                    color: "bg-gradient-primary"
                  },
                  {
                    icon: Zap,
                    title: "One Prompt",
                    description: "Studio spins ad copy, IG captions, SMS lines in one pass.",
                    number: 2,
                    color: "bg-green-500"
                  },
                  {
                    icon: Rocket,
                    title: "Visuals In A Flash",
                    description: "Create all your assets in Visual Lab.",
                    number: 3,
                    color: "bg-blue-500"
                  },
                  {
                    icon: Share2,
                    title: "Edit & Publish",
                    description: "Tweak prices or images, push live to all channels.",
                    number: 4,
                    color: "bg-purple-500"
                  }
                ].map((feature, index) => (
                  <FeatureCard
                    key={index}
                    feature={feature}
                    index={index}
                    className="max-w-none mx-0 w-full"
                  />
                ))}
              </AnimatedContainer>
            </div>
          </div>
        </section>

        {/* Retailer Quotes Section */}
        <section className="py-32 bg-gradient-accent">
          <div className="container mx-auto px-6">
            <motion.div
              className="text-center mb-20"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 id="retail-results-heading" className="text-5xl md:text-6xl font-bold text-foreground mb-6">Real Results from Retailers</h2>
              <p id="retail-results-description" className="text-xl text-muted-foreground max-w-3xl mx-auto">
                See how small businesses are competing with big brands
              </p>
            </motion.div>

            <InfiniteTestimonialSlider
              testimonials={[
                { quote: "Sold out our Mother's Day sale in 48 h with one prompt.", author: "Carlos Ramírez", company: "Store Owner", avatar: "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/62e43e5e-b43e-4269-b914-ceda0818f407/keta_16.jpg?content-type=image%2Fjpeg" },
                { quote: "Week-of promos now take 20 min, not two days.", author: "Sara Kim", company: "Boutique Owner", avatar: "/src/assets/sara-kim-testimonial.jpg" },
                { quote: "We keep pace with chains—minus the agency retainer.", author: "Dev Patel", company: "Retail Manager", avatar: "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/3446d5bd-d452-490c-b56f-2998299f3066/keta_03.jpg?content-type=image%2Fjpeg" },
                { quote: "Online sales increased 200% in 3 months.", author: "Lisa Thomson", company: "Sales Assistant", avatar: "/src/assets/lisa-thomson-testimonial.jpg" },
                { quote: "We compete with big chains now—professionally.", author: "Roberto Martines", company: "Market Owner", avatar: "/src/assets/roberto-martines-testimonial.jpg" }
              ]}
            />
          </div>
        </section>

        {/* CTA Heading Section */}
        <section className="py-16 bg-gradient-hero-background relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-subtle opacity-30"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>

          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 id="retail-cta-heading" className="text-5xl md:text-7xl font-bold text-foreground leading-tight">
                Outshine the giants
                <span className="bg-gradient-primary bg-clip-text text-transparent"> on every channel.</span>
              </h2>
            </motion.div>
          </div>
        </section>

        {/* Pricing Section */}
        <div id="retail-pricing-section">
          <PricingPreview planTypes={['Free', 'Solo', 'Team']} idPrefix="retail-pricing" />
        </div>

        <SeoFaqSection faqs={billingFaqs} />

        <FinalCTA title="Turn products into <br/> scroll-stopping stories and sales." subtitle="Ready to boost your store's reach?" />
        <FooterWithNewsletter />
      </div>
    </LayoutGroup>
  );
};

export default RetailPage;